/**
 * Sandbox connect popup — developer portal test mode.
 *
 * ISOLATION CONTRACT:
 *  - Never reads or writes localStorage['client-metadata'].
 *  - Never uses useUserContext, apiAuthService, or apiConnectService.
 *  - Auth is in-memory only: accessToken state is cleared on sign-out and on mount.
 *  - Always shows the sign-in screen on open — no auto-skip for existing sessions.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { ETwoFAMethod } from '@xeppt/xeppt-sdk/types';

import { sandboxConnectService, ConnectApiError } from '@api';
import type { ConnectSession } from '@api';
import {
    deriveInitials,
    parseMerchantNameHint,
    parseConnectToken,
    parseOriginHint,
    postCancelMessage,
    postPaymentSuccessMessage,
    postSuccessMessage,
    sanitizeMerchantName
} from '@utils/connect_helpers';
import { useApiMutation } from '@hooks/api/useApiMutation';
import { useForm } from '@hooks/helpers/useForm';
import { signInValidation } from '@helpers/validation_objects/signin';
import { useLocales } from '@hooks/helpers/useLocales';
import { defaultSignInData } from '@const/default_form_data';
import { routes } from '@const/routes';

import Button from '@components/common/button';
import Input from '@components/common/input';
import FormField from '@components/form_field';
import MFALayout from '@layouts/auth/signin/mfa';

import logo from '@svg/dark_header_logo.svg';
import checkoutIllustration from '@svg/illustrations/connect/checkout.svg';
import cartIcon from '@svg/icons/connect/cart.svg';
import shieldSecureIcon from '@svg/icons/connect/shield_secure.svg';
import chevronDownIcon from '@svg/icons/connect/chevron_down.svg';
import clockIcon from '@svg/icons/connect/clock.svg';
import walletIcon from '@svg/icons/connect/wallet.svg';
import closeIcon from '@svg/icons/connect/close.svg';
import chevronLeftIcon from '@svg/icons/connect/chevron_left.svg';
import logoutIcon from '@svg/icons/connect/logout.svg';
import illustrationDone from '@svg/illustrations/connect/done.svg';
import illustrationError from '@svg/illustrations/connect/error.svg';
import illustrationExpired from '@svg/illustrations/connect/expired.svg';
import connectStyles from '../connect/styles.module.scss';
import sandboxStyles from './styles.module.scss';

// ─── State machine ────────────────────────────────────────────────────────────

type ConnectState =
    | 'signin'
    | 'mfa'
    | 'loading'
    | 'consent'
    | 'greeting'
    | 'success'
    | 'error'
    | 'expired'
    | 'confirm'
    | 'cart'
    | 'charging'
    | 'paid'
    | 'failed';

type FailureCode = 'insufficient_funds' | 'generic';

interface SandboxMfaState {
    twoFaToken: string;
    method: ETwoFAMethod;
}

// ─── Module-level helpers ─────────────────────────────────────────────────────

function formatAmount(cents: number, currency: string): string {
    const cur = currency || 'CAD';
    return new Intl.NumberFormat('en-CA', {
        style: 'currency',
        currency: cur,
        currencyDisplay: 'narrowSymbol'
    }).format(cents / 100);
}

function formatMoney(cents: number, currency: string): string {
    return `${formatAmount(cents, currency)} ${currency || 'CAD'}`;
}

function formatCountdown(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

const ACTIVE_PAYMENT_STATES: ConnectState[] = ['confirm', 'cart', 'charging'];

// Shared with renderFailedScreen's insufficient_funds case so the pre-charge
// guard and the post-charge failure screen show identical copy.
const INSUFFICIENT_FUNDS_MESSAGE = "You don't have enough balance to complete this payment.";

const ConnectSandboxLayout = () => {
    const location = useLocation();
    const { labelLocale, placeholderLocale, requestErrorLocale } = useLocales();

    // ── Query param extraction (untrusted) ──────────────────────────────────
    const token = parseConnectToken(location.search);
    const originHint = parseOriginHint(location.search);
    const merchantNameHint = parseMerchantNameHint(location.search);

    // ── In-memory auth state — NEVER persisted to localStorage ─────────────
    const [accessToken, setAccessToken] = useState<string | null>(null);
    // Email used to sign in — shown in place of a fetched profile name.
    const [sandboxEmail, setSandboxEmail] = useState('');
    // Best-effort profile fetch — populated after sign-in, cleared on sign-out.
    const [profile, setProfile] = useState<{ firstName: string; lastName: string; avatar: string | null } | null>(null);

    // ── Authoritative session from backend ──────────────────────────────────
    const [session, setSession] = useState<ConnectSession | null>(null);

    // ── State machine — always starts at signin; no auto-skip ───────────────
    const [connectState, setConnectState] = useState<ConnectState>(() => {
        if (!token) return 'error';
        return 'signin';
    });

    const [mfaData, setMfaData] = useState<SandboxMfaState | undefined>(undefined);

    // ── Profile dropdown ────────────────────────────────────────────────────
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // ── Payment-specific state ───────────────────────────────────────────────
    const [failureCode, setFailureCode] = useState<FailureCode>('generic');
    const [chargeResult, setChargeResult] = useState<{
        transaction_id: string;
        amount: number;
        customer_id?: string;
        username?: string;
    } | null>(null);
    const [countdown, setCountdown] = useState<number | null>(null);
    const successFiredRef = useRef(false);
    const chargingRef = useRef(false);
    const greetingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // When "Sign in again" is pressed from the expired screen, route connect
    // errors back to expired (not the generic dead-end) so the user always sees
    // a "Return to store" action.
    const retryingFromExpiredRef = useRef(false);

    // ── Consent-specific state ───────────────────────────────────────────────
    const [isConsentLoading, setIsConsentLoading] = useState(false);
    const consentingRef = useRef(false);

    // ── Payment success poster — fires once, deduplicated via ref ────────────
    const firePaymentSuccess = useCallback(() => {
        if (successFiredRef.current || !chargeResult || !session) return;
        successFiredRef.current = true;
        postPaymentSuccessMessage(
            chargeResult.transaction_id,
            chargeResult.amount,
            session.redirect_origin,
            chargeResult.customer_id,
            chargeResult.username
        );
        window.close();
    }, [chargeResult, session]);

    // ── Cancel / close helper ───────────────────────────────────────────────
    const handleCancel = useCallback(() => {
        if (connectState === 'charging') return;
        if (connectState === 'paid') {
            firePaymentSuccess();
            return;
        }
        if (greetingTimerRef.current) {
            clearTimeout(greetingTimerRef.current);
            greetingTimerRef.current = null;
        }
        const target = session?.redirect_origin ?? originHint;
        postCancelMessage(target);
        window.close();
    }, [connectState, session, originHint, firePaymentSuccess]);

    // ── Escape key → cancel ─────────────────────────────────────────────────
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                handleCancel();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [handleCancel]);

    // ── Focus trap: keep Tab inside the modal ───────────────────────────────
    const modalRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const modal = modalRef.current;
        if (!modal) return;
        modal.focus();
        const handleTab = (e: KeyboardEvent) => {
            if (e.key !== 'Tab') return;
            const focusable = modal.querySelectorAll<HTMLElement>(
                'button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
            );
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (!first || !last) return;
            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        };
        modal.addEventListener('keydown', handleTab);
        return () => modal.removeEventListener('keydown', handleTab);
    }, []);

    // ── Close profile dropdown on click-outside ─────────────────────────────
    useEffect(() => {
        if (!isDropdownOpen) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isDropdownOpen]);

    // ── Connect flow: getSession → branch on payment vs identity ────────────
    // Takes the bearer token as a parameter so the caller does not depend on
    // React having flushed the accessToken state update yet.
    const runConnectFlow = useCallback(
        async (bearerToken: string) => {
            if (!token) {
                setConnectState('error');
                return;
            }
            setConnectState('loading');
            sandboxConnectService.getProfile(bearerToken).then(setProfile).catch(() => {});
            try {
                const sessionData = await sandboxConnectService.getSession(token, bearerToken);
                setSession(sessionData);
                if (
                    typeof sessionData.amount_cents === 'number' &&
                    sessionData.amount_cents > 0
                ) {
                    setConnectState('greeting');
                    greetingTimerRef.current = setTimeout(
                        () => setConnectState('confirm'),
                        1000
                    );
                    return;
                }
                const scopes = (sessionData.scope ?? '').split(' ').filter(Boolean);
                if (scopes.includes('signin')) {
                    setConnectState('consent');
                    return;
                }
                const { code } = await sandboxConnectService.consent(token, bearerToken);
                setConnectState('success');
                setTimeout(() => {
                    postSuccessMessage(code, sessionData.redirect_origin);
                    window.close();
                }, 1000);
            } catch {
                const wasRetryingFromExpired = retryingFromExpiredRef.current;
                retryingFromExpiredRef.current = false;
                setConnectState(wasRetryingFromExpired ? 'expired' : 'error');
            }
        },
        [token]
    );

    // ── Countdown: ticks every second during active payment states ───────────
    useEffect(() => {
        if (!session?.expires_at) return;
        if (!ACTIVE_PAYMENT_STATES.includes(connectState)) return;
        const expiresAt = new Date(session.expires_at).getTime();
        if (isNaN(expiresAt)) return;
        const tick = () => {
            const remaining = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000));
            setCountdown(remaining);
            if (remaining === 0) {
                setConnectState('expired');
            }
        };
        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [connectState, session?.expires_at]);

    // ── Charge handler ───────────────────────────────────────────────────────
    const handleCharge = useCallback(async () => {
        if (chargingRef.current || !token || !session || !accessToken) return;
        chargingRef.current = true;
        setConnectState('charging');
        try {
            const result = await sandboxConnectService.charge(token, accessToken);
            setChargeResult({
                transaction_id: result.transaction_id,
                amount: result.amount,
                customer_id: result.customer_id,
                username: result.username
            });
            setConnectState('paid');
        } catch (err) {
            if (err instanceof ConnectApiError && err.code === 'INSUFFICIENT_FUNDS') {
                setFailureCode('insufficient_funds');
            } else {
                setFailureCode('generic');
            }
            setConnectState('failed');
        } finally {
            chargingRef.current = false;
        }
    }, [token, session, accessToken]);

    // Auto-fire success message ~1.5 s after reaching the paid screen.
    useEffect(() => {
        if (connectState !== 'paid') return;
        const id = setTimeout(firePaymentSuccess, 1500);
        return () => clearTimeout(id);
    }, [connectState, firePaymentSuccess]);

    // ── Consent allow handler ────────────────────────────────────────────────
    const handleConsentAllow = useCallback(async () => {
        if (consentingRef.current || !token || !session || !accessToken) return;
        consentingRef.current = true;
        setIsConsentLoading(true);
        try {
            const { code } = await sandboxConnectService.consent(token, accessToken);
            setConnectState('success');
            setTimeout(() => {
                postSuccessMessage(code, session.redirect_origin);
                window.close();
            }, 1000);
        } catch {
            setConnectState('error');
        } finally {
            consentingRef.current = false;
            setIsConsentLoading(false);
        }
    }, [token, session, accessToken]);

    // ── Sign-in form ─────────────────────────────────────────────────────────
    const {
        control,
        formState: { isValid },
        getValues,
        reset
    } = useForm({
        defaultValues: defaultSignInData,
        resolver: zodResolver(
            signInValidation({
                messages: {
                    email: 'Please enter a valid email',
                    mfa: 'Please enter the verification code'
                },
                isMFA: !!mfaData
            })
        )
    });

    // ── Sign-out / switch account: clear in-memory state, back to sign-in ───
    const handleSandboxSignOut = useCallback(() => {
        setAccessToken(null);
        setSandboxEmail('');
        setProfile(null);
        setMfaData(undefined);
        successFiredRef.current = false;
        chargingRef.current = false;
        consentingRef.current = false;
        retryingFromExpiredRef.current = false;
        setSession(null);
        setChargeResult(null);
        setCountdown(null);
        reset(defaultSignInData);
        setConnectState('signin');
    }, [reset]);

    // ── MFA verify — exchanges the OTP code for an access token ─────────────
    const { handleRequest: handleVerify, isLoading: isVerifyLoading } = useApiMutation({
        method: () =>
            sandboxConnectService.verifyMfa(
                mfaData?.twoFaToken ?? '',
                getValues().mfaCode
            ),
        onSuccess: (result) => {
            setSandboxEmail(getValues().email);
            setAccessToken(result.accessToken);
            void runConnectFlow(result.accessToken);
        },
        onError: requestErrorLocale
    });

    // ── Sign-in submit ────────────────────────────────────────────────────────
    const { handleRequest: handleSubmit, isLoading: isSignInLoading } = useApiMutation({
        method: () => {
            const { email, password } = getValues();
            return sandboxConnectService.login(email, password);
        },
        onSuccess: (result) => {
            if (result.mfaRequired) {
                setMfaData({
                    twoFaToken: result.twoFaToken,
                    method: result.method
                });
                setConnectState('mfa');
            } else {
                const email = getValues().email;
                setSandboxEmail(email);
                setAccessToken(result.accessToken);
                void runConnectFlow(result.accessToken);
            }
        },
        onError: requestErrorLocale
    });

    // ── Merchant display name ────────────────────────────────────────────────
    const merchantName = session?.merchant_name
        ? sanitizeMerchantName(session.merchant_name)
        : merchantNameHint;

    // ── User display — name from the best-effort profile fetch (email
    // fallback); avatar/initials from the authoritative session, which is
    // always populated once loaded (unlike the flaky /profile fetch). ───────
    const displayName = profile && (profile.firstName || profile.lastName)
        ? `${profile.firstName} ${profile.lastName}`.trim()
        : sandboxEmail;
    const avatarUrl = session?.avatar ?? '';
    const displayInitials = session && (session.first_name || session.last_name)
        ? deriveInitials(session.first_name, session.last_name)
        : (sandboxEmail ? deriveInitials(sandboxEmail.split('@')[0], '') : '??');

    // ── Payment session convenience values ───────────────────────────────────
    const currency = session?.currency ?? 'CAD';
    const amountCents = session?.amount_cents ?? 0;
    const walletBalanceCents = session?.wallet_balance_cents ?? 0;
    // Pre-charge guard: the backend re-validates at charge time regardless,
    // but showing this upfront avoids a doomed charge attempt.
    const insufficientBalance = walletBalanceCents < amountCents;

    // ─────────────────────────────────────────────────────────────────────────
    // Payment screen renderers — identical visuals to the live connect popup.
    // ─────────────────────────────────────────────────────────────────────────

    const renderConfirmScreen = () => {
        const isCharging = connectState === 'charging';
        const hasLineItems = (session?.line_items?.length ?? 0) > 0;
        return (
            <div className={connectStyles.payment_confirm} aria-live="polite">
                <div className={connectStyles.top_bar}>
                    <div ref={dropdownRef} className={connectStyles.profile_dropdown_wrapper}>
                        <button
                            type="button"
                            className={connectStyles.avatar_btn}
                            aria-label="User profile menu"
                            aria-haspopup="menu"
                            aria-expanded={isDropdownOpen}
                            disabled={isCharging}
                            onClick={
                                isCharging ? undefined : () => setIsDropdownOpen((prev) => !prev)
                            }>
                            <div className={connectStyles.avatar_sm} aria-hidden="true">
                                {avatarUrl ? <img src={avatarUrl} alt="" className={connectStyles.avatar_sm_img} /> : displayInitials}
                            </div>
                            {/* Figma: "Icons/Chevron Arrow Down 18x18" in Profile row. */}
                            <img
                                src={chevronDownIcon}
                                width={18}
                                height={18}
                                alt=""
                                aria-hidden="true"
                                className={connectStyles.avatar_chevron}
                            />
                        </button>
                        {isDropdownOpen && (
                            <div className={connectStyles.profile_dropdown} role="menu">
                                <div className={connectStyles.dropdown_user_info}>
                                    <p className={connectStyles.dropdown_name}>{displayName}</p>
                                    <p className={connectStyles.dropdown_email}>{sandboxEmail}</p>
                                </div>
                                <button
                                    type="button"
                                    className={connectStyles.dropdown_signout}
                                    role="menuitem"
                                    onClick={() => {
                                        if (isCharging) return;
                                        setIsDropdownOpen(false);
                                        handleSandboxSignOut();
                                    }}>
                                    {/* Figma: "Icons/Logout 24x24" white stroke, left of the button text. */}
                                    <img src={logoutIcon} width={24} height={24} alt="" aria-hidden="true" />
                                    Sign out
                                </button>
                            </div>
                        )}
                    </div>
                    <img src={logo} alt="XEPPT" className={connectStyles.top_logo} />
                    {countdown !== null && (
                        <div
                            className={connectStyles.countdown_pill}
                            role="timer"
                            aria-live="off"
                            aria-label={`Session expires in ${formatCountdown(countdown)}`}>
                            {/* Figma: "Icons/Time Circle 18x18" in countdown pill. */}
                            <img src={clockIcon} width={18} height={18} alt="" aria-hidden="true" />
                            <span>{formatCountdown(countdown)}</span>
                        </div>
                    )}
                </div>
                <div className={connectStyles.order_row}>
                    <span className={connectStyles.order_row_left}>
                        <img src={cartIcon} width={24} height={24} alt="" aria-hidden="true" />
                        {hasLineItems ? (
                            <button
                                type="button"
                                className={connectStyles.order_row_link}
                                onClick={() => setConnectState('cart')}
                                aria-label="View cart details">
                                Your order
                            </button>
                        ) : (
                            <span className={connectStyles.order_row_label}>Your order</span>
                        )}
                    </span>
                    <span className={connectStyles.order_total}>
                        {formatMoney(amountCents, currency)}
                    </span>
                </div>
                <div className={connectStyles.divider} aria-hidden="true" />
                <p className={connectStyles.pay_with_label}>Pay with</p>
                <div className={connectStyles.wallet_row}>
                    <span className={connectStyles.wallet_row_left}>
                        <img src={walletIcon} width={24} height={24} alt="" aria-hidden="true" />
                        <span>XEPPT wallet</span>
                    </span>
                    <span className={connectStyles.wallet_amount}>
                        {formatMoney(Math.max(0, walletBalanceCents), currency)}
                    </span>
                </div>
                {insufficientBalance && (
                    <p className={connectStyles.error_body}>{INSUFFICIENT_FUNDS_MESSAGE}</p>
                )}
                <div className={connectStyles.payment_action}>
                    <Button
                        variant="primary"
                        full
                        isLoading={isCharging}
                        disabled={isCharging || insufficientBalance}
                        onClick={
                            isCharging || insufficientBalance
                                ? undefined
                                : () => { void handleCharge(); }
                        }>
                        Complete payment
                    </Button>
                    <div className={connectStyles.secure_row}>
                        <img src={shieldSecureIcon} width={16} height={16} alt="" aria-hidden="true" />
                        <span>Secure &amp; encrypted by XEPPT</span>
                    </div>
                </div>
                <div className={connectStyles.confirm_cancel}>
                    <button
                        type="button"
                        className={connectStyles.cancel_link}
                        onClick={handleCancel}
                        aria-label={`Cancel and return to ${merchantName || 'the store'}`}>
                        Cancel and return to {merchantName ? merchantName : 'the store'}
                    </button>
                </div>
            </div>
        );
    };

    const renderCartScreen = () => {
        const lineItems = session?.line_items ?? [];
        // The sandbox session carries line items + total but no split breakdown,
        // so derive the subtotal from the items when the backend didn't send one.
        const subtotal =
            session?.subtotal_cents ||
            lineItems.reduce((sum, it) => sum + it.unit_price_cents * it.quantity, 0);
        const shipping = session?.shipping_cents ?? 0;
        const fees = session?.fee_cents ?? 0;
        return (
            <div className={connectStyles.cart_screen} aria-live="polite">
                <button
                    type="button"
                    className={connectStyles.cart_back}
                    onClick={() => setConnectState('confirm')}
                    aria-label="Back to checkout">
                    {/* Figma: "Icons/Chevron Arrow Left 18x18" in cart back button. */}
                    <img src={chevronLeftIcon} width={18} height={18} alt="" aria-hidden="true" />
                    Back to checkout
                </button>
                <p className={connectStyles.cart_heading}>Cart details</p>
                <div className={connectStyles.cart_table_header} aria-hidden="true">
                    <span className={connectStyles.cart_col_item}>Item</span>
                    <span className={connectStyles.cart_col_qty}>Qty</span>
                    <span className={connectStyles.cart_col_price}>Price</span>
                </div>
                {/* Scrollable items region — summary + total stay anchored below */}
                <div className={connectStyles.cart_items_list}>
                    {lineItems.map((item, idx) => (
                        <div key={idx} className={connectStyles.cart_item_row}>
                            <div className={connectStyles.cart_item_info}>
                                <span className={connectStyles.cart_item_name}>{item.name}</span>
                                <span
                                    className={connectStyles.cart_item_desc}
                                    title={item.description}>
                                    {item.description}
                                </span>
                            </div>
                            <span className={connectStyles.cart_item_qty}>×{item.quantity}</span>
                            <span className={connectStyles.cart_item_price}>
                                {formatAmount(item.unit_price_cents * item.quantity, currency)}
                            </span>
                        </div>
                    ))}
                </div>
                <div className={connectStyles.cart_summary_row}>
                    <span className={connectStyles.cart_summary_label}>Subtotal</span>
                    <span className={connectStyles.cart_summary_value}>
                        {formatAmount(subtotal, currency)}
                    </span>
                </div>
                {shipping > 0 && (
                    <div className={connectStyles.cart_summary_row}>
                        <span className={connectStyles.cart_summary_label}>Shipping</span>
                        <span className={connectStyles.cart_summary_value}>
                            {formatAmount(shipping, currency)}
                        </span>
                    </div>
                )}
                {fees > 0 && (
                    <div className={connectStyles.cart_summary_row}>
                        <span className={connectStyles.cart_summary_label}>Estimated fees</span>
                        <span className={connectStyles.cart_summary_value}>
                            {formatAmount(fees, currency)}
                        </span>
                    </div>
                )}
                <div className={connectStyles.divider} aria-hidden="true" />
                <div className={connectStyles.cart_total_row}>
                    <span>Order total</span>
                    <span>{formatMoney(amountCents, currency)}</span>
                </div>
            </div>
        );
    };

    const renderPaidScreen = () => (
        <div className={connectStyles.paid_panel} aria-live="polite">
            <img src={logo} alt="XEPPT" className={connectStyles.logo} />
            {/* Figma: "Illustration/Done 120x120" renders at 100×100. */}
            <img
                src={illustrationDone}
                width={100}
                height={100}
                alt=""
                aria-hidden="true"
                className={connectStyles.state_illustration}
            />
            <p className={connectStyles.paid_title}>Payment successful</p>
            <p className={connectStyles.paid_body}>
                Your payment has been completed successfully.
            </p>
            <Button variant="primary" full onClick={firePaymentSuccess}>
                Return to store
            </Button>
        </div>
    );

    const renderFailedScreen = () => {
        const message =
            failureCode === 'insufficient_funds'
                ? INSUFFICIENT_FUNDS_MESSAGE
                : "We couldn't complete your payment. Please try again later.";
        return (
            <div className={connectStyles.failed_panel} aria-live="assertive">
                <img src={logo} alt="XEPPT" className={connectStyles.logo} />
                {/* Figma: "Illustration/Error 120x120" renders at 100×100. */}
                <img
                    src={illustrationError}
                    width={100}
                    height={100}
                    alt=""
                    aria-hidden="true"
                    className={connectStyles.state_illustration}
                />
                <p className={connectStyles.failed_title}>Payment failed</p>
                <p className={connectStyles.failed_body}>{message}</p>
                <div className={connectStyles.failed_actions}>
                    <Button variant="outlined-primary" size="medium" onClick={handleCancel}>
                        Return to store
                    </Button>
                    <Button
                        variant="primary"
                        size="medium"
                        onClick={() => setConnectState('confirm')}>
                        Try again
                    </Button>
                </div>
            </div>
        );
    };

    const renderExpiredScreen = () => (
        <div className={connectStyles.expired_panel} aria-live="assertive">
            <img src={logo} alt="XEPPT" className={connectStyles.logo} />
            {/* Figma: "Illustration/Payment expired 100x100". */}
            <img
                src={illustrationExpired}
                width={100}
                height={100}
                alt=""
                aria-hidden="true"
                className={connectStyles.state_illustration}
            />
            <p className={connectStyles.failed_title}>Session expired</p>
            <p className={connectStyles.failed_body}>
                Your payment session has expired. Please return to the store to start a new checkout.
            </p>
            <div className={connectStyles.failed_actions}>
                {/* Primary: close the popup so the merchant can initiate a fresh session. */}
                <Button variant="primary" size="medium" onClick={handleCancel}>
                    Return to store
                </Button>
                <Button
                    variant="outlined-primary"
                    size="medium"
                    onClick={() => {
                        retryingFromExpiredRef.current = true;
                        setConnectState('signin');
                    }}>
                    Sign in again
                </Button>
            </div>
        </div>
    );

    const renderGreetingScreen = () => (
        <div className={connectStyles.success_panel} aria-live="polite">
            <img src={logo} alt="XEPPT" className={connectStyles.logo} />
            <div className={connectStyles.avatar} aria-hidden="true">
                {avatarUrl ? (
                    <img src={avatarUrl} alt="" className={connectStyles.avatar_img} />
                ) : (
                    <span className={connectStyles.avatar_initials}>{displayInitials}</span>
                )}
            </div>
            <p className={connectStyles.success_name}>Hello, {displayName}!</p>
            <p className={connectStyles.success_body}>
                You&apos;ve successfully signed in. Getting your XEPPT wallet&hellip;
            </p>
            <button
                type="button"
                className={connectStyles.cancel_link}
                onClick={handleCancel}
                aria-label={`Cancel and return to ${merchantName || 'the store'}`}>
                Cancel and return to {merchantName ? merchantName : 'the store'}
            </button>
        </div>
    );

    const renderConsentScreen = () => (
        <div className={connectStyles.success_panel} aria-live="polite">
            <img src={logo} alt="XEPPT" className={connectStyles.logo} />
            <p className={connectStyles.title}>Connect with {merchantName}</p>
            <p className={connectStyles.subtitle}>
                By continuing, you allow {merchantName} to send money to your XEPPT wallet, now
                and in the future.
            </p>
            <p className={connectStyles.consent_reassurance}>
                {merchantName} will know you have a XEPPT wallet and can send funds to it —
                shown only as a secure reference, never your username, email, or balance.
            </p>
            <Button
                className={connectStyles.submit_btn}
                variant="primary"
                full
                isLoading={isConsentLoading}
                onClick={() => { void handleConsentAllow(); }}>
                Allow
            </Button>
            <button
                type="button"
                className={connectStyles.cancel_link}
                onClick={handleCancel}
                aria-label={`Cancel and return to ${merchantName || 'the store'}`}>
                Cancel and return to {merchantName ? merchantName : 'the store'}
            </button>
        </div>
    );

    const renderLeftContent = () => {
        switch (connectState) {
            case 'confirm':
            case 'charging':
                return renderConfirmScreen();

            case 'cart':
                return renderCartScreen();

            case 'paid':
                return renderPaidScreen();

            case 'failed':
                return renderFailedScreen();

            case 'expired':
                return renderExpiredScreen();

            case 'greeting':
                return renderGreetingScreen();

            case 'consent':
                return renderConsentScreen();

            case 'success':
                return (
                    <div className={connectStyles.success_panel} aria-live="polite">
                        <div className={connectStyles.avatar} aria-hidden="true">
                            {avatarUrl ? (
                                <img src={avatarUrl} alt="" className={connectStyles.avatar_img} />
                            ) : (
                                <span className={connectStyles.avatar_initials}>
                                    {displayInitials}
                                </span>
                            )}
                        </div>
                        <p className={connectStyles.success_name}>Hello, {displayName}!</p>
                        <p className={connectStyles.success_body}>
                            You&apos;ve successfully signed in.
                        </p>
                        <p className={connectStyles.success_body}>
                            Getting your XEPPT wallet&hellip;
                        </p>
                    </div>
                );

            case 'error':
                return (
                    <div className={connectStyles.error_panel} aria-live="assertive">
                        <p className={connectStyles.error_title}>
                            {!token ? 'Invalid request' : 'Session expired'}
                        </p>
                        <p className={connectStyles.error_body}>
                            {!token
                                ? 'No connect token was found in this URL. Please return to the store and try again.'
                                : 'Your session has expired or the connect request is no longer valid.'}
                        </p>
                        <div className={connectStyles.error_actions}>
                            <Button
                                variant="outlined-primary"
                                size="medium"
                                onClick={handleCancel}>
                                Return to store
                            </Button>
                            {token && (
                                <Button
                                    variant="primary"
                                    size="medium"
                                    onClick={() => setConnectState('signin')}>
                                    Sign in again
                                </Button>
                            )}
                        </div>
                    </div>
                );

            case 'loading':
                return (
                    <div
                        className={connectStyles.success_panel}
                        aria-live="polite"
                        aria-busy="true">
                        <p className={connectStyles.subtitle}>
                            Connecting to your XEPPT account&hellip;
                        </p>
                    </div>
                );

            case 'mfa':
                return (
                    <MFALayout
                        method={mfaData?.method ?? ETwoFAMethod.EMAIL}
                        control={control}
                        isLoading={isVerifyLoading}
                        onSubmit={() => handleVerify(undefined)}
                        onFallback={() => {
                            reset({ ...getValues(), mfaCode: '' });
                            setMfaData(undefined);
                            setConnectState('signin');
                        }}
                    />
                );

            case 'signin':
            default:
                return (
                    <>
                        <img src={logo} alt="XEPPT" className={connectStyles.logo} />
                        <div className={sandboxStyles.sandbox_badge}>TEST MODE &mdash; sandbox</div>
                        <p className={connectStyles.title}>Sign in</p>
                        <p className={connectStyles.subtitle}>
                            to your XEPPT account to continue
                        </p>
                        <div
                            className={connectStyles.form}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && isValid) {
                                    handleSubmit(undefined);
                                }
                            }}>
                            <FormField
                                control={control as any}
                                name="email"
                                renderComponent={(props) => (
                                    <Input
                                        {...props}
                                        full
                                        label={labelLocale('email')}
                                        placeholder={placeholderLocale('enter_email')}
                                        name="email"
                                        autoComplete="email"
                                        autoFocus
                                    />
                                )}
                            />
                            <FormField
                                control={control as any}
                                name="password"
                                renderComponent={(props) => (
                                    <Input
                                        {...props}
                                        full
                                        type="password"
                                        label={labelLocale('password')}
                                        placeholder={placeholderLocale('enter_password')}
                                        name="password"
                                        autoComplete="current-password"
                                    />
                                )}
                            />
                        </div>
                        <Button
                            className={connectStyles.submit_btn}
                            variant="primary"
                            full
                            disabled={!isValid}
                            isLoading={isSignInLoading}
                            onClick={() => handleSubmit(undefined)}>
                            Sign in
                        </Button>
                        <button
                            type="button"
                            className={connectStyles.cancel_link}
                            onClick={handleCancel}
                            aria-label={`Cancel and return to ${merchantName || 'the store'}`}>
                            Cancel and return to{' '}
                            {merchantName ? merchantName : 'the store'}
                        </button>
                    </>
                );
        }
    };

    const showFooter = [
        'signin',
        'consent',
        'error',
        'expired',
        'greeting',
        'confirm',
        'charging',
        'cart',
        'paid',
        'failed'
    ].includes(connectState);

    const dialogLabel =
        connectState === 'confirm' || connectState === 'charging'
            ? 'Complete your payment with XEPPT'
            : connectState === 'cart'
              ? 'Cart details'
              : connectState === 'paid'
                ? 'Payment successful'
                : connectState === 'failed'
                  ? 'Payment failed'
                  : connectState === 'expired'
                    ? 'Session expired'
                    : connectState === 'greeting'
                      ? 'Signed in to XEPPT'
                      : connectState === 'consent'
                        ? 'Connect with XEPPT'
                        : 'Sign in with XEPPT — Sandbox';

    return (
        <div className={connectStyles.page}>
            <div
                ref={modalRef}
                className={connectStyles.modal}
                role="dialog"
                aria-modal="true"
                aria-label={dialogLabel}
                tabIndex={-1}>
                {/* Left column */}
                <section className={connectStyles.left}>
                    {renderLeftContent()}
                    {showFooter && (
                        <footer className={connectStyles.footer}>
                            <span className={connectStyles.footer_text}>&copy; 2026 XEPPT</span>
                            <button
                                type="button"
                                className={connectStyles.footer_link}
                                onClick={() =>
                                    window.open(routes.terms_of_service, '_blank', 'noopener')
                                }>
                                Terms of Service
                            </button>
                            <button
                                type="button"
                                className={connectStyles.footer_link}
                                onClick={() =>
                                    window.open(routes.privacy_policy, '_blank', 'noopener')
                                }>
                                Privacy Policy
                            </button>
                        </footer>
                    )}
                </section>

                {/* Right column */}
                <section className={connectStyles.right} aria-hidden="true">
                    <button
                        type="button"
                        className={connectStyles.close_btn}
                        onClick={handleCancel}
                        aria-label="Close"
                        aria-hidden="false">
                        <img src={closeIcon} width={24} height={24} alt="" aria-hidden="true" />
                    </button>
                    <img
                        src={checkoutIllustration}
                        alt=""
                        className={connectStyles.illustration}
                        aria-hidden="true"
                    />
                    <p className={connectStyles.right_heading}>
                        Pay with confidence using XEPPT
                    </p>
                    <p className={connectStyles.right_subtitle}>
                        Fast checkout with built-in security
                    </p>
                </section>
            </div>
        </div>
    );
};

export default ConnectSandboxLayout;
